/**
 * Founder announcement modal — shows the active announcement for the
 * signed-in user once, then never again (dismissal is tracked per-user
 * server-side). Same provider shape as `WelcomeModal`: auto-fires off a
 * TanStack query, dismiss → POST seen → invalidate.
 *
 * Content is server-driven (admin Compose tab), so this renders whatever
 * the founder wrote: a title, a markdown body, and an optional CTA button
 * (internal route → navigate; external URL → open).
 *
 * Mounted via `<AnnouncementModalProvider>` in App.tsx, after the welcome
 * + first-leak modals so onboarding takes priority on a brand-new user's
 * first session.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import {
  getActiveAnnouncement,
  markAnnouncementSeen,
  type Announcement,
} from "@/shared/announcements";
import { useAuth } from "@/features/auth/lib/authContext";
import { ICON_STROKE, X } from "@/shared/components/icons";
import { LogoMark } from "@/shared/components/LogoMark";

export function AnnouncementModalProvider({ children }: { children: ReactNode }) {
  const { status: authStatus } = useAuth();
  const signedIn = authStatus.kind === "signedIn";
  const queryClient = useQueryClient();

  const { data: announcement } = useQuery({
    queryKey: ["announcement-active"],
    queryFn: getActiveAnnouncement,
    enabled: signedIn,
    staleTime: 60_000,
  });

  const [open, setOpen] = useState(false);
  const hasOpenedRef = useRef(false);

  useEffect(() => {
    if (!signedIn || !announcement) return;
    if (!hasOpenedRef.current) {
      hasOpenedRef.current = true;
      setOpen(true);
    }
  }, [signedIn, announcement]);

  const dismiss = async () => {
    setOpen(false);
    if (announcement) {
      try {
        await markAnnouncementSeen(announcement.id);
      } catch {
        // Silent — re-fires next session at worst, not a bug.
      }
      queryClient.invalidateQueries({ queryKey: ["announcement-active"] });
    }
  };

  return (
    <>
      {children}
      {open && announcement && (
        <AnnouncementModalView announcement={announcement} onDismiss={dismiss} />
      )}
    </>
  );
}

function AnnouncementModalView({
  announcement,
  onDismiss,
}: {
  announcement: Announcement;
  onDismiss: () => Promise<void>;
}) {
  const navigate = useNavigate();

  const onCta = async () => {
    const url = announcement.ctaUrl;
    await onDismiss();
    if (!url) return;
    if (url.startsWith("/")) navigate(url);
    else window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onDismiss}
    >
      <div
        className="w-full max-w-md max-h-[90dvh] flex flex-col rounded-2xl border border-accent/40 bg-surface relative shadow-[0_28px_60px_-20px_rgba(124,147,241,0.6)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="absolute top-3 right-3 w-10 h-10 flex items-center justify-center text-text-muted hover:text-text"
        >
          <X size={18} strokeWidth={ICON_STROKE} />
        </button>

        <div className="flex-1 overflow-y-auto p-6 pt-8 space-y-4">
          <div className="mx-auto w-12 h-12 flex items-center justify-center">
            <LogoMark size={44} animate={false} />
          </div>
          <h2 className="text-xl font-bold text-text tracking-tight text-center">
            {announcement.title}
          </h2>
          <div className="prose-chat text-sm text-text-muted leading-relaxed">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {announcement.body}
            </ReactMarkdown>
          </div>
        </div>

        <div className="px-6 pt-2 pb-5 border-t border-border/40 flex gap-2">
          {announcement.ctaLabel && announcement.ctaUrl ? (
            <>
              <button
                type="button"
                onClick={onDismiss}
                className="flex-1 py-3 rounded-xl border border-border text-text-muted text-sm font-medium hover:border-text-muted/40 hover:text-text transition"
              >
                Dismiss
              </button>
              <button
                type="button"
                onClick={onCta}
                className="flex-1 py-3 rounded-xl bg-accent text-white font-medium hover:bg-accent/90 transition shadow-[0_1px_0_rgba(255,255,255,0.18)_inset,0_8px_20px_-8px_rgba(124,147,241,0.6)]"
              >
                {announcement.ctaLabel}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onDismiss}
              className="flex-1 py-3 rounded-xl bg-accent text-white font-medium hover:bg-accent/90 transition"
            >
              Got it
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
